/// <reference types="youtube" />
/* eslint-disable no-undef */

/**
 * YouTube iframe API script loader
 * Dynamically loads the YouTube iframe API script and provides a promise-based interface
 */

declare global {
  interface Window {
    YT: typeof YT;
    onYouTubeIframeAPIReady: () => void;
  }
}

let apiReady = false;
let apiLoading = false;
const readyCallbacks: Array<() => void> = [];

/**
 * Loads the YouTube iframe API script if not already loaded
 * @returns Promise that resolves when the API is ready
 */
export function loadYouTubeAPI(): Promise<void> {
  return new Promise((resolve, reject) => {
    // If API is already ready, resolve immediately
    if (apiReady && window.YT && window.YT.Player) {
      resolve();
      return;
    }

    // If API is currently loading, add to callbacks
    if (apiLoading) {
      readyCallbacks.push(resolve);
      return;
    }

    // Start loading the API
    apiLoading = true;

    // Set up the global callback
    window.onYouTubeIframeAPIReady = () => {
      if (process.env.NODE_ENV === 'development') {
        console.log('[youtubeApiLoader] YouTube iframe API ready');
      }
      apiReady = true;
      apiLoading = false;
      resolve();

      // Call all pending callbacks
      readyCallbacks.forEach(callback => callback());
      readyCallbacks.length = 0;
    };

    // Create and append the script tag
    const script = document.createElement('script');
    script.src = 'https://www.youtube.com/iframe_api';
    script.async = true;
    script.onerror = () => {
      apiLoading = false;
      reject(new Error('Failed to load YouTube iframe API'));
    };

    // Check if script already exists
    const existingScript = document.querySelector('script[src="https://www.youtube.com/iframe_api"]');
    if (existingScript) {
      // Script exists but API might not be ready yet
      if (window.YT && window.YT.Player) {
        apiReady = true;
        apiLoading = false;
        resolve();
      } else {
        // Wait for the existing script's callback
        readyCallbacks.push(resolve);
        // Set a timeout in case the callback never fires
        setTimeout(() => {
          if (window.YT && window.YT.Player && !apiReady) {
            apiReady = true;
            apiLoading = false;
            resolve();
            readyCallbacks.forEach(callback => callback());
            readyCallbacks.length = 0;
          }
        }, 5000);
      }
      return;
    }

    // Append script to document
    const firstScript = document.getElementsByTagName('script')[0];
    if (firstScript && firstScript.parentNode) {
      firstScript.parentNode.insertBefore(script, firstScript);
    } else {
      document.head.appendChild(script);
    }
  });
}

/**
 * Checks if YouTube API is ready
 * @returns true if API is loaded and ready
 */
export function isYouTubeAPIReady(): boolean {
  return apiReady && !!window.YT && !!window.YT.Player;
}

